import { ScrollPageContainer } from '@repo/ui/components/molecules/scroll-page-container';
import { PageHeader } from '@repo/ui/components/molecules/page-header';
import { Tabs, TabsList, TabsTrigger } from '@repo/ui/components/ui/tabs';
import { SKIP, TAKE } from '@/lib/constants/pagination';
import { FORECAST, PROTECTED } from '@/lib/constants/routes';
import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  forecastLayoutSearchSchema,
  FORECAST_LAYOUT_TABS,
  FORECAST_TAB_RESET_PARAMS,
} from '@/routes/-shared/forecastLayoutSearchSchema';
import { resolveForecastLayoutRedirect } from '@/routes/-shared/forecast-layout-defaults';
import {
  CashFlowForecast,
  ForecastPlanning,
  ForecastRecurrences,
} from '@/pages/forecast';
import { DscrDashboardContent } from '@/pages/dscr';
import { RoleGuard } from '@/components/molecules/role-guard';
import { usePermissions } from '@/hooks/use-permissions';
import { resolveAuthorizedTab } from '@/lib/permissions/resolve-authorized-tab';
import { buildForecastTabAccess } from '@/routes/-shared/forecast-tab-access';

export const Route = createFileRoute('/protected/_layout/forecast/_layout')({
  validateSearch: forecastLayoutSearchSchema,
  beforeLoad: ({ search, location }) => {
    const target = resolveForecastLayoutRedirect({
      pathname: location.pathname,
      search,
      currentYear: new Date().getFullYear(),
    });
    if (target) {
      throw redirect({
        to: target.to,
        search: target.search,
        replace: true,
      } as never);
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { canAccessForecast, canViewCashFlowForecast, canViewDscr, isLoading } =
    usePermissions();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const params = useSearch({
    from: '/protected/_layout/forecast/_layout',
  });

  const currentTab = params.tab ?? FORECAST_LAYOUT_TABS.FORECAST;

  const resetFilters = FORECAST_TAB_RESET_PARAMS;

  const tabAccess = buildForecastTabAccess({
    canAccessForecast,
    canViewCashFlowForecast,
    canViewDscr,
  });
  const hasTabAccess = (value: string) =>
    tabAccess.some((tab) => tab.value === value && tab.hasAccess);

  const tabs = [
    {
      value: FORECAST_LAYOUT_TABS.FORECAST,
      text: t('cashFlowForecast.top_tabs.forecast'),
      params: { tab: FORECAST_LAYOUT_TABS.FORECAST, ...resetFilters },
    },
    {
      value: FORECAST_LAYOUT_TABS.PLANNING,
      text: t('cashFlowForecast.top_tabs.planning'),
      params: { tab: FORECAST_LAYOUT_TABS.PLANNING, ...resetFilters },
    },
    {
      value: FORECAST_LAYOUT_TABS.RECURRENCES,
      text: t('cashFlowForecast.top_tabs.recurrences'),
      params: { tab: FORECAST_LAYOUT_TABS.RECURRENCES, ...resetFilters },
    },
    {
      value: FORECAST_LAYOUT_TABS.DSCR,
      text: t('cashFlowForecast.top_tabs.dscr'),
      params: { tab: FORECAST_LAYOUT_TABS.DSCR, ...resetFilters },
    },
  ].filter((tab) => hasTabAccess(tab.value));

  const accessState = resolveAuthorizedTab(currentTab, tabAccess);

  useEffect(() => {
    if (
      isLoading ||
      accessState.hasRequestedAccess ||
      !accessState.fallbackTab
    ) {
      return;
    }
    navigate({
      to: `${PROTECTED}${FORECAST}`,
      search: (previous) => ({
        ...previous,
        tab: accessState.fallbackTab,
      }),
      replace: true,
    });
  }, [
    accessState.fallbackTab,
    accessState.hasRequestedAccess,
    isLoading,
    navigate,
  ]);

  if (
    !isLoading &&
    accessState.hasAnyAccess &&
    !accessState.hasRequestedAccess
  ) {
    return null;
  }

  const handleTabChange = (value: string) => {
    const tab = tabs.find((item) => item.value === value);
    if (!tab) return;
    navigate({
      to: `${PROTECTED}${FORECAST}`,
      search: (previous: Record<string, unknown>) => ({
        ...previous,
        ...tab.params,
        skip: SKIP,
        take: TAKE,
      }),
      replace: true,
    });
  };

  const renderTabContent = () => {
    switch (currentTab) {
      case FORECAST_LAYOUT_TABS.PLANNING:
        return (
          <>
            <ForecastPlanning />
            <Outlet />
          </>
        );
      case FORECAST_LAYOUT_TABS.RECURRENCES:
        return (
          <>
            <ForecastRecurrences />
            <Outlet />
          </>
        );
      case FORECAST_LAYOUT_TABS.DSCR:
        return (
          <>
            <DscrDashboardContent />
            <Outlet />
          </>
        );
      case FORECAST_LAYOUT_TABS.FORECAST:
      default:
        return (
          <>
            <CashFlowForecast />
            <Outlet />
          </>
        );
    }
  };

  return (
    <RoleGuard hasAccess={accessState.hasRequestedAccess} isLoading={isLoading}>
      <ScrollPageContainer tablePage className="gap-4">
        <div className="flex shrink-0 flex-col">
          <PageHeader title={t('cashFlowForecast.page_title')} />
          <Tabs value={currentTab} onValueChange={handleTabChange}>
            <TabsList
              aria-label={t('cashFlowForecast.page_title')}
              className="max-w-full overflow-x-auto overflow-y-hidden border-border"
            >
              {tabs.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.text}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
        <div
          data-forecast-wrapper
          className="max-w-screen flex-1 overflow-hidden flex flex-col"
        >
          {renderTabContent()}
        </div>
      </ScrollPageContainer>
    </RoleGuard>
  );
}
